// apps/admin/src/api/support-agents.api.ts
//
// Phase 9.3 Wave 7 Task 13 — Support Agent Assignment admin API client.
// NEW client for the agent-config + routing-rule endpoints (P9.3.D-07,
// P9.3.D-08). Mirrors support-tickets.api.ts envelope-unwrap + CSRF
// double-submit interceptor pattern; the corresponding BE routes live in
// apps/api/src/modules/support (Tasks 05–10 — canonical contract).
//
// Consumed by all FE surfaces in later waves (AgentsTab task-17,
// AgentConfigModal task-14, AddAgentModal task-15, RoutingRuleEditor
// task-16, topbar availability toggle task-18). Pure `.ts` — no UI.
//
// Zod schemas below MIRROR the API-CONTRACTS response `data` shapes
// VERBATIM incl. the per-agent computed fields (activeTicketCount /
// atCap / withinWorkingHours). Enum VALUES are the English wire contract
// (Order|Billing|Product|Account, category|channel|sla, agents|escalate,
// inapp|telegram|lark); FE localizes LABELS only via t() in components.
//
// vg-binding: PLAN:task-13
// vg-binding: P9.3.D-07   — agent config + routing-rule endpoint surface
// vg-binding: P9.3.D-08   — self-availability toggle + query-key factory
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:message-priority
//
// Transport contract:
//   • A local `agentsAxios` clone keeps the `{ ok, data, meta }` envelope
//     intact so listAgents can surface `meta` (page/pageSize/total) to
//     the TanStack hook + pagination footer, and re-attaches the CSRF
//     double-submit header on mutating verbs.
//   • `unwrapSupportEnvelope` (shared with support-tickets.api.ts) drives
//     the INTERFACE-STANDARDS message priority
//     (error.user_message > error.message > message > network_fallback);
//     toasts consume the returned `userMessage` directly.

import axios from 'axios';
import { z } from 'zod';

import { API_BASE_URL } from '../lib/apiClient.js';
import {
  TicketCategorySchema,
  newIdempotencyKey,
  unwrapSupportEnvelope,
  type SupportTicketApiError,
  type TicketCategory,
} from './support-tickets.api.js';

// ---------------------------------------------------------------------------
// Enum VALUES — English wire contract (NOT localized). FE maps to labels
// via t() in the consuming components (Tasks 14–17).
// ---------------------------------------------------------------------------

/** Routing rule condition dimension. */
export const RoutingRuleTypeSchema = z.enum(['category', 'channel', 'sla']);
export type RoutingRuleType = z.infer<typeof RoutingRuleTypeSchema>;

/** Routing target resolution — assign to explicit agents OR escalate pool. */
export const RoutingTargetKindSchema = z.enum(['agents', 'escalate']);
export type RoutingTargetKind = z.infer<typeof RoutingTargetKindSchema>;

/** Notify-channel transport (P9.3.D-09). */
export const NotifyChannelKindSchema = z.enum(['inapp', 'telegram', 'lark']);
export type NotifyChannelKind = z.infer<typeof NotifyChannelKindSchema>;

// ---------------------------------------------------------------------------
// Response schemas — mirror API-CONTRACTS `data` shapes VERBATIM.
// (unwrapSupportEnvelope already strips the {ok,data,meta} envelope, so
//  these parse the inner `data` payload.)
// ---------------------------------------------------------------------------

/** notifyChannels entry — inapp needs no target; telegram/lark carry one. */
export const NotifyChannelSchema = z.object({
  kind: NotifyChannelKindSchema,
  target: z.string().optional(),
});
export type NotifyChannel = z.infer<typeof NotifyChannelSchema>;

/** One working-hours window within a tz (day 0–6, HH:mm start/end). */
export const WorkingHoursWindowSchema = z.object({
  day: z.number().int().min(0).max(6),
  start: z.string().regex(/^\d{2}:\d{2}$/),
  end: z.string().regex(/^\d{2}:\d{2}$/),
});
export type WorkingHoursWindow = z.infer<typeof WorkingHoursWindowSchema>;

export const WorkingHoursSchema = z.object({
  tz: z.string().min(1), // IANA, e.g. "Asia/Ho_Chi_Minh"
  windows: z.array(WorkingHoursWindowSchema).default([]),
});
export type WorkingHours = z.infer<typeof WorkingHoursSchema>;

/**
 * One roster row = config (or backward-compat defaults) + computed fields.
 * `categories: []` => ALL; `maxConcurrent: null` => ∞. Computed fields
 * (activeTicketCount / atCap / withinWorkingHours) are derived server-side
 * per TS-21.
 */
export const SupportAgentRowSchema = z.object({
  userId: z.string(), // Mongo ObjectId hex
  name: z.string(),
  email: z.string().email(),
  roleType: z.string(), // display-only; NOT mutated by add/remove
  isActive: z.boolean(),
  available: z.boolean(),
  workingHours: WorkingHoursSchema.nullable(),
  categories: z.array(TicketCategorySchema), // [] => ALL
  maxConcurrent: z.number().int().min(1).nullable(), // null => ∞
  channels: z.array(z.string()), // support_channels ids
  notifyChannels: z.array(NotifyChannelSchema),
  // ---- computed (TS-21) ----
  activeTicketCount: z.number().int().min(0), // {Open,InProgress,Reopened}
  atCap: z.boolean(), // activeTicketCount >= maxConcurrent
  withinWorkingHours: z.boolean(), // now within a tz window (or true if none)
});
export type SupportAgentRow = z.infer<typeof SupportAgentRowSchema>;

/** Pagination block for GET /agents. */
export const SupportAgentListMetaSchema = z.object({
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type SupportAgentListMeta = z.infer<typeof SupportAgentListMetaSchema>;

export interface SupportAgentListPayload {
  rows: SupportAgentRow[];
  meta: SupportAgentListMeta;
}

/** POST /agents result — roleType echoed unchanged (membership ≠ role grant). */
export const AddAgentResultSchema = z.object({
  userId: z.string(),
  name: z.string(),
  roleType: z.string(),
  isActive: z.literal(true),
  available: z.boolean(),
  createdAt: z.string().datetime(),
});
export type AddAgentResult = z.infer<typeof AddAgentResultSchema>;

/** PUT /agents/:userId/config result. */
export const PutAgentConfigResultSchema = z.object({
  userId: z.string(),
  isActive: z.boolean(),
  available: z.boolean(),
  maxConcurrent: z.number().int().min(1).nullable(),
  categories: z.array(TicketCategorySchema),
  channels: z.array(z.string()),
  updatedAt: z.string().datetime(),
});
export type PutAgentConfigResult = z.infer<typeof PutAgentConfigResultSchema>;

/** DELETE /agents/:userId result — force-reassign report (TS-06). */
export const RemoveAgentResultSchema = z.object({
  userId: z.string(),
  isActive: z.literal(false),
  roleType: z.string(), // echoed unchanged — proof no role strip
  forceReassignedCount: z.number().int().min(0),
  reassignedTicketIds: z.array(z.string()),
});
export type RemoveAgentResult = z.infer<typeof RemoveAgentResultSchema>;

/** PATCH /agents/me/availability result — userId resolved from token. */
export const MyAvailabilityResultSchema = z.object({
  userId: z.string(),
  available: z.boolean(),
  isActive: z.boolean(),
  updatedAt: z.string().datetime(),
});
export type MyAvailabilityResult = z.infer<typeof MyAvailabilityResultSchema>;

/** Routing target block (shared by list + mutation shapes). */
export const RoutingTargetSchema = z.object({
  kind: RoutingTargetKindSchema,
  agentIds: z.array(z.string()), // required when kind='agents'
});
export type RoutingTarget = z.infer<typeof RoutingTargetSchema>;

/** GET /routing-rules row + POST result (pre-sorted order asc, _id asc). */
export const SupportRoutingRuleSchema = z.object({
  id: z.string(), // Mongo ObjectId hex
  type: RoutingRuleTypeSchema,
  match: z.string().min(1), // category id | channel id | sla tier
  target: RoutingTargetSchema,
  order: z.number().int(),
  createdAt: z.string().datetime(),
});
export type SupportRoutingRule = z.infer<typeof SupportRoutingRuleSchema>;

/** PUT /routing-rules/:id result — same shape with updatedAt in place of createdAt. */
export const RoutingRuleUpdateResultSchema = z.object({
  id: z.string(),
  type: RoutingRuleTypeSchema,
  match: z.string().min(1),
  target: RoutingTargetSchema,
  order: z.number().int(),
  updatedAt: z.string().datetime(),
});
export type RoutingRuleUpdateResult = z.infer<
  typeof RoutingRuleUpdateResultSchema
>;

/** DELETE /routing-rules/:id result. */
export const DeleteRoutingRuleResultSchema = z.object({
  id: z.string(),
  deleted: z.literal(true),
});
export type DeleteRoutingRuleResult = z.infer<
  typeof DeleteRoutingRuleResultSchema
>;

// ---------------------------------------------------------------------------
// Request shapes (FE input) — mirror the API-CONTRACTS request bodies.
// ---------------------------------------------------------------------------

export interface ListAgentsFilters {
  /** true => available only; false => unavailable only; omit => both. */
  available?: boolean;
  category?: TicketCategory;
  /** Search by agent name/email. */
  q?: string;
  sort?: 'name' | 'activeTicketCount' | 'maxConcurrent';
  dir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export interface AddAgentRequest {
  /** Target User (must be internal-staff) — Mongo ObjectId hex. */
  userId: string;
  available?: boolean;
  categories?: TicketCategory[];
  /** >=1 when set; null => ∞. */
  maxConcurrent?: number | null;
}

export interface PutAgentConfigRequest {
  available: boolean;
  workingHours: { tz: string; windows: WorkingHoursWindow[] };
  /** ⊆ TICKET_CATEGORIES; [] => ALL. */
  categories: TicketCategory[];
  /** >=1 when set; 0 => 422 (TS-11); null => ∞. */
  maxConcurrent: number | null;
  channels: string[];
  notifyChannels: { kind: NotifyChannelKind; target?: string }[];
}

export interface RoutingRuleTargetInput {
  kind: RoutingTargetKind;
  /** Required (>=1) when kind='agents'; ignored for 'escalate'. */
  agentIds: string[];
}

export interface CreateRoutingRuleRequest {
  type: RoutingRuleType;
  match: string; // non-empty; empty => 422 (TS-22)
  target: RoutingRuleTargetInput;
  order: number;
}

export interface UpdateRoutingRuleRequest {
  type?: RoutingRuleType;
  match?: string;
  target?: RoutingRuleTargetInput;
  order?: number;
}

export interface ListRoutingRulesFilters {
  type?: RoutingRuleType;
  sort?: 'order' | 'createdAt';
  dir?: 'asc' | 'desc';
}

// ---------------------------------------------------------------------------
// Local axios clone — keeps both `data` AND `meta` on the wire and echoes
// the CSRF double-submit header on mutating verbs (mirrors
// support-tickets.api.ts rawAxios). CSRF header is unnecessary on GETs.
// ---------------------------------------------------------------------------

const NETWORK_FALLBACK = 'Network did not respond — please try again.';

const NETWORK_ERROR: SupportTicketApiError = {
  ok: false,
  code: 'NETWORK_ERROR',
  userMessage: NETWORK_FALLBACK,
};

function invalidPayload(userMessage: string): SupportTicketApiError {
  return { ok: false, code: 'INVALID_PAYLOAD', userMessage };
}

const agentsAxios = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30_000,
  headers: { Accept: 'application/json' },
});

// CSRF double-submit interceptor — the shared apiClient handles this on the
// main instance, but this local clone bypasses that pipeline. Mutating verbs
// read the XSRF-TOKEN cookie (NOT HttpOnly) and echo it as X-XSRF-TOKEN; the
// BE auth plugin enforces the match (else 403 AUTH_CSRF_MISSING).
function readSupportCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]*)/);
  return m && typeof m[1] === 'string' ? decodeURIComponent(m[1]) : null;
}
agentsAxios.interceptors.request.use((cfg) => {
  const method = (cfg.method ?? 'get').toUpperCase();
  if (
    method === 'POST' ||
    method === 'PUT' ||
    method === 'PATCH' ||
    method === 'DELETE'
  ) {
    const csrf = readSupportCsrfToken();
    if (csrf) {
      cfg.headers = cfg.headers ?? {};
      // Both Axios v0.x (plain object) + v1.x (AxiosHeaders) paths.
      if (
        typeof (cfg.headers as { set?: (k: string, v: string) => void }).set ===
        'function'
      ) {
        (cfg.headers as { set: (k: string, v: string) => void }).set(
          'X-XSRF-TOKEN',
          csrf,
        );
      } else {
        (cfg.headers as Record<string, string>)['X-XSRF-TOKEN'] = csrf;
      }
    }
  }
  return cfg;
});

function buildQs(params: Record<string, string>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, v);
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

/** GET helper — returns the unwrapped `{ data, meta }` or throws a SupportTicketApiError. */
async function agentsGet(
  path: string,
): Promise<{ data: unknown; meta: unknown }> {
  let body: unknown;
  try {
    const res = await agentsAxios.get(path);
    body = res.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapSupportEnvelope<unknown, unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
    }
    throw NETWORK_ERROR;
  }
  const unwrap = unwrapSupportEnvelope<unknown, unknown>(body);
  if (!unwrap.ok) throw unwrap;
  return { data: unwrap.data, meta: unwrap.meta };
}

/** Mutation helper (POST/PUT/PATCH/DELETE) — returns the unwrapped `data` or throws. */
async function agentsSend(
  method: 'post' | 'put' | 'patch' | 'delete',
  path: string,
  payload?: unknown,
): Promise<unknown> {
  let body: unknown;
  try {
    const r =
      method === 'delete'
        ? await agentsAxios.delete(path)
        : await agentsAxios[method](path, payload ?? {}, {
            headers: { 'Idempotency-Key': newIdempotencyKey() },
          });
    body = r.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapSupportEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
      body = err.response.data;
    } else {
      throw NETWORK_ERROR;
    }
  }
  const unwrap = unwrapSupportEnvelope<unknown>(body);
  if (!unwrap.ok) throw unwrap;
  return unwrap.data;
}

// ===========================================================================
// Resource: support_agent (agents)
// ===========================================================================

/**
 * GET /api/v1/tickets/agents — list agents (active config LEFT-JOIN roster)
 * with per-agent computed fields. Returns `{ rows, meta }`; meta is
 * synthesized defensively if the BE omits the pagination block.
 */
export async function listAgents(
  filters: ListAgentsFilters = {},
): Promise<SupportAgentListPayload> {
  const params: Record<string, string> = {};
  if (typeof filters.available === 'boolean') {
    params.available = filters.available ? 'true' : 'false';
  }
  if (filters.category) params.category = filters.category;
  if (filters.q) params.q = filters.q;
  if (filters.sort) params.sort = filters.sort;
  if (filters.dir) params.dir = filters.dir;
  if (filters.page) params.page = String(filters.page);
  if (filters.pageSize) params.pageSize = String(filters.pageSize);

  const { data, meta } = await agentsGet(
    `/api/v1/tickets/agents${buildQs(params)}`,
  );

  const rows = z.array(SupportAgentRowSchema).safeParse(data);
  if (!rows.success) {
    throw invalidPayload('Agent list response had an invalid format.');
  }
  const metaParsed = SupportAgentListMetaSchema.safeParse(meta);
  const metaVal: SupportAgentListMeta = metaParsed.success
    ? metaParsed.data
    : {
        page: filters.page ?? 1,
        pageSize: filters.pageSize ?? 20,
        total: rows.data.length,
      };
  return { rows: rows.data, meta: metaVal };
}

/** POST /api/v1/tickets/agents — add an agent (config row; internal-staff only). */
export async function addAgent(payload: AddAgentRequest): Promise<AddAgentResult> {
  const data = await agentsSend('post', '/api/v1/tickets/agents', payload);
  const parsed = AddAgentResultSchema.safeParse(data);
  if (!parsed.success) {
    throw invalidPayload('Add-agent response had an invalid format.');
  }
  return parsed.data;
}

/** PUT /api/v1/tickets/agents/:userId/config — upsert per-agent config (idempotent). */
export async function putAgentConfig(
  userId: string,
  payload: PutAgentConfigRequest,
): Promise<PutAgentConfigResult> {
  const data = await agentsSend(
    'put',
    `/api/v1/tickets/agents/${encodeURIComponent(userId)}/config`,
    payload,
  );
  const parsed = PutAgentConfigResultSchema.safeParse(data);
  if (!parsed.success) {
    throw invalidPayload('Agent config response had an invalid format.');
  }
  return parsed.data;
}

/**
 * DELETE /api/v1/tickets/agents/:userId — remove an agent (isActive=false +
 * force-reassign open tickets). Optional `expectedOpenTickets` echoes the
 * UI pre-confirm count.
 */
export async function removeAgent(
  userId: string,
  opts: { expectedOpenTickets?: number } = {},
): Promise<RemoveAgentResult> {
  const qs =
    typeof opts.expectedOpenTickets === 'number'
      ? buildQs({ expectedOpenTickets: String(opts.expectedOpenTickets) })
      : '';
  const data = await agentsSend(
    'delete',
    `/api/v1/tickets/agents/${encodeURIComponent(userId)}${qs}`,
  );
  const parsed = RemoveAgentResultSchema.safeParse(data);
  if (!parsed.success) {
    throw invalidPayload('Remove-agent response had an invalid format.');
  }
  return parsed.data;
}

/**
 * PATCH /api/v1/tickets/agents/me/availability — self-toggle availability.
 * "me" resolved from the auth token; never sends a userId.
 */
export async function patchMyAvailability(
  available: boolean,
): Promise<MyAvailabilityResult> {
  const data = await agentsSend(
    'patch',
    '/api/v1/tickets/agents/me/availability',
    { available },
  );
  const parsed = MyAvailabilityResultSchema.safeParse(data);
  if (!parsed.success) {
    throw invalidPayload('Availability response had an invalid format.');
  }
  return parsed.data;
}

// ===========================================================================
// Resource: support_routing_rule (routing-rules)
// ===========================================================================

/** GET /api/v1/tickets/routing-rules — list rules (pre-sorted order asc, _id asc). */
export async function listRoutingRules(
  filters: ListRoutingRulesFilters = {},
): Promise<SupportRoutingRule[]> {
  const params: Record<string, string> = {};
  if (filters.type) params.type = filters.type;
  if (filters.sort) params.sort = filters.sort;
  if (filters.dir) params.dir = filters.dir;

  const { data } = await agentsGet(
    `/api/v1/tickets/routing-rules${buildQs(params)}`,
  );
  const parsed = z.array(SupportRoutingRuleSchema).safeParse(data);
  if (!parsed.success) {
    throw invalidPayload('Routing rules response had an invalid format.');
  }
  return parsed.data;
}

/** POST /api/v1/tickets/routing-rules — create a conditional routing rule. */
export async function createRoutingRule(
  payload: CreateRoutingRuleRequest,
): Promise<SupportRoutingRule> {
  const data = await agentsSend(
    'post',
    '/api/v1/tickets/routing-rules',
    payload,
  );
  const parsed = SupportRoutingRuleSchema.safeParse(data);
  if (!parsed.success) {
    throw invalidPayload('Routing rule create response had an invalid format.');
  }
  return parsed.data;
}

/** PUT /api/v1/tickets/routing-rules/:id — update a rule (partial). */
export async function updateRoutingRule(
  id: string,
  payload: UpdateRoutingRuleRequest,
): Promise<RoutingRuleUpdateResult> {
  const data = await agentsSend(
    'put',
    `/api/v1/tickets/routing-rules/${encodeURIComponent(id)}`,
    payload,
  );
  const parsed = RoutingRuleUpdateResultSchema.safeParse(data);
  if (!parsed.success) {
    throw invalidPayload('Routing rule update response had an invalid format.');
  }
  return parsed.data;
}

/** DELETE /api/v1/tickets/routing-rules/:id — delete a rule. */
export async function deleteRoutingRule(
  id: string,
): Promise<DeleteRoutingRuleResult> {
  const data = await agentsSend(
    'delete',
    `/api/v1/tickets/routing-rules/${encodeURIComponent(id)}`,
  );
  const parsed = DeleteRoutingRuleResultSchema.safeParse(data);
  if (!parsed.success) {
    throw invalidPayload('Routing rule delete response had an invalid format.');
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// Test seam — internals exported for unit tests only.
// ---------------------------------------------------------------------------

export const __test__ = { agentsAxios, buildQs };
